import { Component, inject } from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, ValidatorFn, Validators} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgFor, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { HttpServiceService } from '../../services/http-service.service';
import { Permission, RolePermissions } from '../../models/permissions.model';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { LocalStorageService } from '../../services/local-storage.service';
import { USER_PERMISSIONS } from '../../app.constants';



@Component({
  selector: 'app-assign-permissions',
  imports: [MatFormFieldModule,  MatSelectModule, FormsModule, ReactiveFormsModule, NgFor, NgIf, BreadcrumbComponent],
  templateUrl: './assign-permissions.component.html',
  styleUrl: './assign-permissions.component.css'
})
export class AssignPermissionsComponent {
  breadcrumbList = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'User Permissions',
    breadcrumb_url : '/admin/tenants'
  }
  searchControl = new FormControl('');
  toppings = new FormControl('');
  allPermissionsSubscription? : Subscription;
  getRolePermissionsSubscription? : Subscription;
  updateRolePermissionsSubscription? : Subscription;
  private httpService = inject(HttpServiceService); 
  private authService = inject(LocalStorageService); 
  private getUserPermissionsSubscription? : Subscription;

  setPermissionsForm: FormGroup | undefined;
  filteredPermissionsList : Permission[] = [];
  filteredItems: Permission[] = [];  

  constructor(private fb: FormBuilder, private alertService: NgxToastrService) {  }

  ngOnInit(){
    this.getAllPermissions();    
  }

  createForm() {
    this.setPermissionsForm = this.fb.group({
      searchText : this.fb.control(''),
      role : this.fb.control('', Validators.required),
      selectedPermissions: this.fb.array(this.filteredPermissionsList.map(() => this.fb.control(false)),
      this.minOneCheckboxChecked())  // FormArray for checkboxes
    });
  }

  minOneCheckboxChecked(): ValidatorFn {
    return (control: AbstractControl) => {
      const formArray = control as FormArray;
      return formArray.controls.some(ctrl => ctrl.value) ? null : { required: true };
    };
  }


  setCheckboxes(rolePermissions : Permission[]) {
    this.allFormControls.controls.forEach((control, index) => {      
      let isChecked = rolePermissions.find(role => role.uid == this.filteredPermissionsList[index].uid) ? true : false;
      if(isChecked) {
        control.setValue(true);
      }else{
        control.setValue(false);
      }
    });
  }  

  getAllPermissions(){
    this.allPermissionsSubscription = this.httpService.getPermissions().subscribe({
      next : (response : RolePermissions) => {
        this.filteredItems =  response.data;
        this.filteredPermissionsList = [...response.data];       
        this.createForm();
      },
      error : (error) => {

      }
    })
  }

  getRolePermissions(){
    let roleIdentifier = this.setPermissionsForm?.get('role')?.value;
    if(roleIdentifier){
      this.httpService.getRolePermissions(roleIdentifier).subscribe({
        next : (response) => {          
          this.setCheckboxes(response.data);
        },
        error : (error) => {          
          this.setCheckboxes([]);
        }
      })
    }    
  }

  

  submitForm() {    
    if (this.setPermissionsForm?.valid) {
      let obj = {
        roleIdentifier : this.setPermissionsForm.value?.role,
        featureUids : this.getSelectedPermissions()
      }      
      this.httpService.updateRolePermissions(obj).subscribe({
        next : (response) => {
          this.alertService.success(response.message, "toast-top-right");
          this.getUpdatedPermissionsList();
        },
        error : (error) => {          
          console.log(error?.error);
        }
      })
    }
  }

  getUpdatedPermissionsList(){
    this.getUserPermissionsSubscription = this.httpService.getUserPermissions().subscribe({
      next : (response) => {
        this.authService.setUserPermissions(response.data?.["permissions"] ?? []);
      }
    })
  }

  filterItems() {
    const control = this.setPermissionsForm?.get("searchText");   
    if (!control?.value) {
      this.filteredItems = this.filteredPermissionsList;
    } else {
      this.filteredItems = this.filteredPermissionsList.filter(item =>
        item.name.toLowerCase().includes(control?.value?.toLowerCase())
      );
    }
  }

  getSelectedPermissions() {
    const selectedItems: string[] = [];
    this.allFormControls.controls.forEach((control, index) => {
      if (control.value) {       
        selectedItems.push(this.filteredPermissionsList[index].uid);
      }
    });
    return selectedItems;
  }

  ngOnDestroy() : void {
    this.allPermissionsSubscription?.unsubscribe();
    this.getRolePermissionsSubscription?.unsubscribe();
    this.updateRolePermissionsSubscription?.unsubscribe();
    this.getUserPermissionsSubscription?.unsubscribe();
  }

  get allFormControls() {
    return this.setPermissionsForm?.get('selectedPermissions') as FormArray;
  }

  get checkViewPermission(){
    return this.authService.checkPermission(USER_PERMISSIONS.PERMISSIONS.VIEW_PERMISSIONS);
  }

  get checkUpdatePermission(){
    return this.authService.checkPermission(USER_PERMISSIONS.PERMISSIONS.UPDATE_PERMISSIONS);
  }

  get noPermission(){
    return USER_PERMISSIONS.ERROR_MSGS.PERMISSION_NOT_FOUND;
  }
 

}
